<template>
  <div class="switch-regions">
    <HKLSelect
      v-if="isHKlandUser"
      ref="multiSelectSwitch"
      v-model="selectedRegion"
      :options="regionOpts"
      :align="'right'"
      :style="{'min-width': `${minWidth}px`}"
      :dropdownMatchSelectWidth="false"
      @select="switchRegion"
    />
  </div>
</template>

<script>
import { computed, getCurrentInstance, nextTick, onMounted, ref, unref, watch } from 'vue-demi'
import { mapActions, mapGetters } from 'vuex'
import HKLSelect from '@/controls/Select/HKLSelect'
import loginMixin from '@/pages/loginMixin'
import { useStoreGetters } from '@/store/index'
import { resetForm, resetSearchResultStore } from '@/store/util'
import EventBus, { EVENT_FLAG } from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import { useActiveRegionOpts } from '@/store/modules/regions.hook'

export default {
  name: 'SwitchRegions',
  components: {
    HKLSelect,
  },
  mixins: [loginMixin],
  setup () {
    const selectedRegion = ref('')
    const regionToGo = ref({})
    const { currentRegion } = useStoreGetters('regions', ['currentRegion'])

    const { proxy } = getCurrentInstance()
    const regionOpts = useActiveRegionOpts()
    const minWidth = computed(() => {
      const size = 9
      return Math.max(...unref(regionOpts).map(el => unref(el.name).length)) * size
    })
    const updateSelectedRegion = (regionName) => {
      const targetRegion = unref(regionOpts).find(item => item.key === regionName)
      if (targetRegion) {
        selectedRegion.value = targetRegion.key
        regionToGo.value = targetRegion
      }
    }
    const resetSelectorToCurRegion = () => {
      proxy.setActiveCurrentRegion(unref(currentRegion).name)
      nextTick(() => updateSelectedRegion(unref(currentRegion).name))
    }
    onMounted(() => {
      updateSelectedRegion(unref(currentRegion).name)
    })
    watch(regionOpts, () => {
      updateSelectedRegion(unref(currentRegion).name)
    })
    watch(currentRegion, () => {
      nextTick(() => updateSelectedRegion(unref(currentRegion).name))
    })
    return {
      regionToGo,
      selectedRegion,
      regionOpts,
      updateSelectedRegion,
      resetSelectorToCurRegion,
      minWidth,
    }
  },
  computed: {
    ...mapGetters('user', ['isHKlandUser', 'userRegions', 'userAdId', 'currentGlobalUser']),
    ...mapGetters('regions', ['currentRegion']),
    ...mapGetters('form', { isDirty: 'isPageDirty' }),
  },
  async mounted () {
    EventBus.$on('component_confirm_continue_pageLevel', this.onComponentConfirmContinue)
  },
  destroyed () {
    EventBus.$off('component_confirm_continue_pageLevel', this.onComponentConfirmContinue)
  },
  methods: {
    ...mapActions('user', ['reloadUser']),
    ...mapActions('language', ['switchLang']),
    ...mapActions('regions', ['initRegionData']),
    ...mapActions('buildings', ['getAllBuildings']),
    ...mapActions('currencies', ['loadPurchaseCurrencies']),
    async initDataForRegion () {
      await this.switchLang()
      await Promise.all([
        // To update the latest buildings in different regions for Vuex
        this.getAllBuildings(),
        // To update the latest currencies in different regions for Vuex
        this.loadPurchaseCurrencies(),
      ])
    },
    async changeCurrentRegion (changingRegion) {
      await this.checkIfRedirectToOtherSite({
        ...changingRegion,
        name: changingRegion.key,
      }, async () => {
        try {
          const changingRegionName = changingRegion.key
          if (this.isActiveRegion(changingRegionName.toUpperCase())) {
            await this.tokenLogin()
            const user = await this.refreshUserStore(this.currentGlobalUser.AD_id)
            await this.initDataForRegion()
            await this.redirectToDashboard(user, () => {
              EventBus.$emit(EVENT_FLAG.SYS_SWITCH_REGION_FINISHED)
              resetSearchResultStore()
            })
          } else {
            notification.warn(i18n.t('hint.regionNotActive'))
            this.resetSelectorToCurRegion()
          }
        } catch (err) {
          console.error(err)
        }
      }, () => {
        this.resetSelectorToCurRegion()
      })
    },
    async switchRegion (key) {
      const changingRegion = this.regionOpts.find((o) => o.key === key)
      if (this.isDirty) {
        this.regionToGo = changingRegion
        this.setActiveCurrentRegion(this.selectedRegion.key)
        EventBus.$emit('modules_confirm_continue_do_nav', {src: 'pageLevel'})
        return
      }
      resetForm()
      await this.changeCurrentRegion(changingRegion)
    },
    async onComponentConfirmContinue () {
      await this.changeCurrentRegion(this.regionToGo)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/settings';
@import 'styles';

.switch-regions {
  margin-right: 25px;
  @include header-selector;
  margin-bottom: 1px;
}

</style>
